import React, { useEffect, useState } from 'react';
import { Modal, Button, Table, Select, Form, message } from 'antd';
import ImageUploader from './ImageUploader';
import { SiteType } from 'graphql/types/SiteType';
import { hasHeadquartersAccess, isFinancialTeam, isKAQC, isCSFollowUp, isCS } from 'utils/auth';
import { useTranslation } from 'react-i18next';
import { useStatusInternationalization } from 'pages/delivery/parcels/hooks/useStatusInternationalization';
import { useWarehouseContext } from '../contexts/WarehouseContext';
import { useGetAllAccessHubAndWarehousesForRole } from '../hooks/useWarehouseQuery';

// 显示模式枚举
enum DisplayMode {
  SHOW_HUB_WAREHOUSE = 'show-hub-warehouse',
  HIDE_HUB_WAREHOUSE = 'hide-hub-warehouse'
}

interface ParcelRow {
  orderId: number;
  trackingNumber: string;
  defaultReason?: string;
  existingImageUrls?: string[];
}

/** 需必选二级原因的异常码：
 * ERDL001/ERDSP004 地址异常
 * ERDL002/ERDSP005 当日无法进入
 * ERDL006/ERDSP008 地址无法投递
 */
const ADDRESS_EXCEPTION_CODES = ['ERDL001', 'ERDSP004', 'ERDL002', 'ERDSP005', 'ERDL006', 'ERDSP008'];
const MULTI_SELECT_SUB_REASON_CODES = ['ERDL001', 'ERDSP004'];

export interface BatchMarkExceptionInput {
  orderId: number;
  exceptionReasonCode: string;
  exceptionHubId: string;
  exceptionWarehouseId?: string | null;
  exceptionImageS3KeysJson: string[];
  exceptionSubReasons?: string[] | null;
}

export interface ExceptionSubReasonType {
  code: string;
  textZh: string;
  textEn: string;
  textEs: string;
}

export interface ExceptionDictionaryType {
  exceptionReasonCode: string;
  primaryType: string;
  secondaryTypeZh: string;
  secondaryTypeEn: string;
  isUploadsImages: boolean;
  exceptionSubReasons?: ExceptionSubReasonType[];
}

export interface WarehouseType {
  id: string;
  name: string;
  siteType: SiteType;
  parentSiteId: string;
}

interface BatchMarkAsExceptionModalProps {
  visible: boolean;
  parcels: ParcelRow[];
  onOk: (orders: BatchMarkExceptionInput[]) => void;
  onCancel: () => void;
  loading?: boolean;
  userRole: string;
  exceptionSelectData: ExceptionDictionaryType[];
}

interface FormValues {
  exceptionReasonCode: string | null;
  exceptionSubReasonCodes: string[];
  exceptionHubId: string | null;
  exceptionWarehouseId: string | null;
  parcels: {
    orderId: number;
    exceptionImageS3KeysJson: string[];
  }[];
}

const BatchMarkAsExceptionModal: React.FC<BatchMarkAsExceptionModalProps> = ({
  visible,
  parcels,
  onOk,
  onCancel,
  loading = false,
  userRole,
  exceptionSelectData
}) => {
  const [form] = Form.useForm<FormValues>();
  // // 仓库列表状态管理
  const [warehouseListForHubData, setWarehouseListForHubData] = useState<any[]>([]);
  const isHeadquartersStaffRole = hasHeadquartersAccess();
  const isKAQCRole = isKAQC();
  const isCSRole = isCS();
  const isFinancialTeamRole = isFinancialTeam();
  const isCSFollowUpRole = isCSFollowUp();
  const {t} = useTranslation();
  const { getExceptionReasonText, getExceptionSubReasonTextByCode } = useStatusInternationalization();

  // 获取当前仓库信息
  const { currentWarehouse } = useWarehouseContext();

  // 判断显示模式
  const getDisplayMode = (): DisplayMode => {
    // 情况1：总部角色（包括CS、KAQC、CSFollowUp等）且当前仓数据为空
    if ((isHeadquartersStaffRole || isKAQCRole || isCSRole || isFinancialTeamRole || isCSFollowUpRole) && !currentWarehouse) {
      return DisplayMode.SHOW_HUB_WAREHOUSE;
    }

    // 默认情况：隐藏hub和warehouse
    return DisplayMode.HIDE_HUB_WAREHOUSE;
  };

  const displayMode = getDisplayMode();

  // 数据查询 - 统一使用 useGetAllAccessHubAndWarehousesForRole
  const {
    warehouses: warehouseListData,
    loading: warehousesLoading,
    error: warehousesError
  } = useGetAllAccessHubAndWarehousesForRole({
    skip: displayMode === DisplayMode.HIDE_HUB_WAREHOUSE
  });

  useEffect(() => {
    if (visible) {
      form.resetFields();
      const initialValues: FormValues = {
        exceptionReasonCode: null,
        exceptionSubReasonCodes: [],
        exceptionHubId: null,
        exceptionWarehouseId: null,
        parcels: parcels.map(row => ({
          orderId: row.orderId,
          exceptionImageS3KeysJson: []
        }))
      };
      form.setFieldsValue(initialValues);
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // 监听表单 Hub 字段变化，自动设置对应的仓库列表
  useEffect(() => {
    if (visible && warehouseListData && displayMode === DisplayMode.SHOW_HUB_WAREHOUSE) {
      const hubId = form.getFieldValue('exceptionHubId');
      if (hubId) {
        const warehouseData = warehouseListData.filter(warehouse => warehouse.siteType === SiteType.WAREHOUSE);
        const filteredWarehouses = warehouseData.filter(warehouse => warehouse.parentSiteId === hubId);
        setWarehouseListForHubData(filteredWarehouses);
      }
    }
  }, [visible, warehouseListData, form, displayMode]);


  // 当数据加载完成后，更新 warehouse 列表
  useEffect(() => {
    if (visible && warehouseListData && warehouseListData.length > 0) {
      if (displayMode === DisplayMode.SHOW_HUB_WAREHOUSE) {
        // 情况1：初始时清空warehouse列表，等待用户选择hub
        setWarehouseListForHubData([]);
      }
    }
  }, [visible, warehouseListData, displayMode, currentWarehouse]);

  const isPhotoDisabled = (reasonCode: string) => {
    const reason = exceptionSelectData.find(ex => ex.exceptionReasonCode === reasonCode);
    return reason ? !reason.isUploadsImages : false;
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const { exceptionReasonCode, exceptionSubReasonCodes, exceptionHubId, exceptionWarehouseId, parcels: parcelValues } = values;

      // 检查必需字段
      if (!exceptionReasonCode) {
        return;
      }

      const isAddressException = ADDRESS_EXCEPTION_CODES.includes(exceptionReasonCode);
      if (isAddressException && (!exceptionSubReasonCodes?.length)) {
        message.warning(t('central.exceptions.messages.pleaseSelectExceptionSubReason'));
        return;
      }

      // 非Missing类型必须上传图片
      if (!isPhotoDisabled(exceptionReasonCode)) {
        const missingPhotos = parcelValues.some(row => !row.exceptionImageS3KeysJson?.length);
        if (missingPhotos) {
          message.error(t('central.exceptions.messages.pleaseUploadPhotosForAllParcels'));
          return;
        }
      }

      const normalizedSubReasonCodes = Array.isArray(exceptionSubReasonCodes) ? exceptionSubReasonCodes : exceptionSubReasonCodes ? [exceptionSubReasonCodes] : [];
      const exceptionSubReasons = isAddressException && normalizedSubReasonCodes.length ? normalizedSubReasonCodes : undefined;

      // 构造提交数据
      const submitData = parcelValues.map(row => ({
        ...row,
        exceptionReasonCode: exceptionReasonCode!,
        exceptionHubId: exceptionHubId!,
        exceptionWarehouseId: exceptionWarehouseId || null,
        // 如果是Missing相关原因，确保图片列表为空数组
        exceptionImageS3KeysJson: isPhotoDisabled(exceptionReasonCode!) ? [] : row.exceptionImageS3KeysJson,
        exceptionSubReasons
      }));

      onOk(submitData);
    } catch (error) {
      console.log('Please complete all required fields.');
      message.error(t('central.exceptions.messages.pleaseCompleteAllRequiredFields'));
    }
  };

  const handleReasonChange = (value: string) => {
    // 只重置异常原因、二级原因和包裹的图片，保持 Hub 和 Warehouse 选择不变
    const currentHubId = form.getFieldValue('exceptionHubId');
    const currentWarehouseId = form.getFieldValue('exceptionWarehouseId');
    
    const initialValues: FormValues = {
      exceptionHubId: currentHubId,
      exceptionWarehouseId: currentWarehouseId,
      exceptionReasonCode: value,
      exceptionSubReasonCodes: [],
      parcels: parcels.map(row => ({
        orderId: row.orderId,
        exceptionImageS3KeysJson: []
      }))
    };
    form.setFieldsValue(initialValues);
  };

  const selectedReasonCode = Form.useWatch('exceptionReasonCode', form);
  const selectedException = exceptionSelectData.find(e => e.exceptionReasonCode === selectedReasonCode);
  const isAddressException = selectedReasonCode != null && ADDRESS_EXCEPTION_CODES.includes(selectedReasonCode);
  const addressSubReasons = selectedException?.exceptionSubReasons ?? [];
  const isMultiSelectSubReason = selectedReasonCode != null && MULTI_SELECT_SUB_REASON_CODES.includes(selectedReasonCode);

  // Hub 切换处理函数
  const handleHubChange = (value: string) => {
    // 获取该hubId对应的warehouse
    const filteredWarehouses = warehouseListData?.filter(warehouse => warehouse.parentSiteId === value) || [];
    setWarehouseListForHubData(filteredWarehouses);
    // 清空仓库选择，因为 hub 改变了
    form.setFieldsValue({
      exceptionWarehouseId: undefined
    });
  };

  const columns = [
    {
      title: t('central.exceptions.columns.tracking'),
      dataIndex: 'orderId',
      align: 'left' as const,
      width: 260,
      render: (_: any, record: any, idx: number) => parcels[idx]?.trackingNumber || record.orderId
    },
    {
      title: t('central.exceptions.columns.reason'),
      dataIndex: 'exceptionReasonCode',
      align: 'left' as const,
      width: 260,
      render: () => {
        const reasonCode = form.getFieldValue('exceptionReasonCode');
        const reason = exceptionSelectData.find(ex => ex.exceptionReasonCode === reasonCode);
        return reason ? getExceptionReasonText(reason, reason.exceptionReasonCode) : '';
      }
    },
    {
      title: t('central.exceptions.columns.photos'),
      dataIndex: 'exceptionImageS3KeysJson',
      align: 'left' as const,
      width: 260,
      render: (_: any, __: any, idx: number) => {
        const reasonCode = form.getFieldValue('exceptionReasonCode');
        const disabled = isPhotoDisabled(reasonCode);
        
        if (disabled) {
          return <span className="text-gray-500">{t('central.exceptions.messages.noPhotoRequired')}</span>;
        }

        return (
          <Form.Item
            name={['parcels', idx, 'exceptionImageS3KeysJson']}
            rules={[{ required: true, message: t('central.exceptions.messages.photoRequired') || '' }]}
          >
            <ImageUploader maxCount={2} />
          </Form.Item>
        );
      }
    }
  ];

  return (
    <Modal
      title={<div className="flex items-center"><div className="w-1 h-6 bg-indigo-600 mr-2"></div><span>{t('central.exceptions.pageDetails.markException')}</span></div>}
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={loading}>{t('central.exceptions.pageDetails.cancel')}</Button>,
        <Button key="ok" type="primary" onClick={handleOk} loading={loading}>{t('central.exceptions.pageDetails.ok')}</Button>
      ]}
      maskClosable={!loading}
      closable={!loading}
    >
      <Form form={form} layout="vertical">
        <div className="mb-4">{t('central.exceptions.pageDetails.markException')} {parcels.length} {t('central.exceptions.pageDetails.parcels')}</div>
        <Form.Item
          label={t('central.exceptions.pageDetails.reason')+':'}
          name="exceptionReasonCode"
          rules={[{ required: true, message: t('central.exceptions.messages.pleaseSelectExceptionReason') || '' }]}
        >
          <Select
            style={{ width: 260 }}
            onChange={handleReasonChange}
            placeholder={t('central.exceptions.pageDetails.selectReason')}
          >
            {exceptionSelectData.map(ex => {
              const label = ex.secondaryTypeEn ? `${ex.secondaryTypeEn} (${ex.exceptionReasonCode})` : 
                           ex.secondaryTypeZh ? `${ex.secondaryTypeZh} (${ex.exceptionReasonCode})` : '-';
              return (
                <Select.Option key={ex.exceptionReasonCode} value={ex.exceptionReasonCode}>
                  {getExceptionReasonText(ex, ex.exceptionReasonCode)}
                </Select.Option>
              );
            })}
          </Select>
        </Form.Item>

        {isAddressException && (
          <Form.Item
            label={t('central.exceptions.pageDetails.exceptionSubReason')+':'}
            name="exceptionSubReasonCodes"
            rules={[{
              required: true,
              message: t('central.exceptions.messages.pleaseSelectExceptionSubReason') || '',
              ...(isMultiSelectSubReason ? { type: 'array' as const, min: 1 } : {})
            }]}
          >
            <Select
              mode={isMultiSelectSubReason ? "multiple" : undefined}
              style={{ width: '100%' }}
              placeholder={t('central.exceptions.pageDetails.selectExceptionSubReason') || ''}
              allowClear
              maxTagCount="responsive"
            >
              {addressSubReasons.map((sub) => (
                <Select.Option key={sub.code} value={sub.code}>
                  {selectedException ? getExceptionSubReasonTextByCode(selectedException, sub.code) : sub.code}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {/* 根据显示模式决定是否显示Hub和Warehouse选择器 */}
        {displayMode !== DisplayMode.HIDE_HUB_WAREHOUSE && (
          <>
          <Form.Item
           label={t('central.exceptions.pageDetails.hub')+':'}
           name="exceptionHubId"
           rules={[{ required: true, message: t('central.exceptions.messages.pleaseSelectExceptionHub') || '' }]}
          >
            <Select
              style={{ width: 260 }}
              placeholder={t('central.exceptions.pageDetails.selectHub')}
              allowClear
              onChange={handleHubChange}
            >
              {
                warehouseListData?.filter(it => it.siteType === SiteType.HUB).map(hub => {
                  return (
                    <Select.Option key={hub.id} value={hub.id} >
                      {hub.name}
                    </Select.Option>
                  )
                })
              }
            </Select>
          </Form.Item>

          <Form.Item
            label={t('central.exceptions.pageDetails.warehouse')+':'}
            name="exceptionWarehouseId"
          >
            <Select
              style={{ width: 260 }}
             placeholder={t('central.exceptions.pageDetails.selectWarehouse')}
              allowClear
            >
                           {/* 只有当 hub 被选择时才显示仓库列表 */}
             {form.getFieldValue('exceptionHubId') && warehouseListForHubData.length > 0 && (
               <>
                 {/* 仓库列表 */}
                 {warehouseListForHubData.map(warehouse => (
                   <Select.Option
                     key={warehouse.id}
                     value={warehouse.id}
                   >
                     {warehouse.name}
                   </Select.Option>
                 ))}
               </>
             )}
            </Select>
          </Form.Item>
        </>
        )}

        <Form.Item name="parcels" noStyle>
          <Table
            dataSource={parcels}
            columns={columns}
            rowKey={(_, idx) => (idx !== undefined ? idx.toString() : '')}
            pagination={false}
            size="small"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default BatchMarkAsExceptionModal; 